<script setup lang="ts">
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import {
    colors,
    primaryColors,
    grayColors,
    PrimaryColorValue,
    GrayColor,
    getPrimaryColorSwatch,
    isHexColor,
    normalizeHexColor,
} from "./colors";
import { Check, Copy } from "lucide-vue-next";
import { Input } from "@/components/ui/input";
import { ref, watch } from "vue";
import Button from "@/components/ui/button/Button.vue";

const primaryColor = defineModel<PrimaryColorValue>("primary", {
    default: "red",
});

const grayColor = defineModel<GrayColor>("gray", {
    default: "slate",
});

const copied = ref(false);

const hexInputValue = ref(
    getPrimaryColorSwatch(primaryColor.value).toUpperCase(),
);

watch(primaryColor, (newVal) => {
    hexInputValue.value = getPrimaryColorSwatch(newVal).toUpperCase();
});

function onHexInputBlur(): void {
    const val = hexInputValue.value.trim();

    if (isHexColor(val)) {
        primaryColor.value = normalizeHexColor(val) as PrimaryColorValue;
    } else {
        hexInputValue.value = getPrimaryColorSwatch(
            primaryColor.value,
        ).toUpperCase();
    }
}

function onNativeColorChange(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (target.value) {
        primaryColor.value = normalizeHexColor(
            target.value,
        ) as PrimaryColorValue;
    }
}

async function copyColorToClipboard(): Promise<void> {
    const color = getPrimaryColorSwatch(primaryColor.value).toUpperCase();

    await navigator.clipboard.writeText(color);

    copied.value = true;

    window.setTimeout(() => {
        copied.value = false;
    }, 1200);
}
</script>
<template>
    <div class="gap-4 flex flex-col">
        <div>
            <p class="mb-1 text-lg font-medium">
                {{ $t("appearances.color_picker.primary") }}
            </p>

            <RadioGroup v-model="primaryColor" :default-value="primaryColor">
                <RadioGroupLabel class="sr-only">{{
                    $t("appearances.color_picker.primary")
                }}</RadioGroupLabel>
                <div class="gap-2 grid grid-cols-11">
                    <RadioGroupOption
                        v-for="option in primaryColors"
                        :key="option"
                        v-slot="{ checked }"
                        as="template"
                        :value="option"
                    >
                        <RadioGroupLabel
                            class="rounded-lg shadow-sm flex aspect-square w-full cursor-pointer items-center justify-center transition-all"
                            :class="[
                                checked
                                    ? 'ring-2 ring-offset-2'
                                    : 'hover:scale-105',
                            ]"
                            :style="{
                                backgroundColor: getPrimaryColorSwatch(option),
                                ...(checked
                                    ? {
                                          '--tw-ring-color':
                                              getPrimaryColorSwatch(option),
                                      }
                                    : {}),
                            }"
                        >
                            <Check
                                v-if="checked"
                                :size="16"
                                class="text-white"
                            />
                        </RadioGroupLabel>
                    </RadioGroupOption>
                </div>
            </RadioGroup>

            <div
                class="mt-3 rounded-xl bg-iron-100 p-2 gap-2 flex items-center"
            >
                <label
                    class="h-9 w-9 rounded-lg shadow-sm relative shrink-0 cursor-pointer overflow-hidden"
                >
                    <input
                        type="color"
                        class="sr-only"
                        :value="getPrimaryColorSwatch(primaryColor)"
                        @input="onNativeColorChange"
                    />
                    <div
                        class="h-full w-full"
                        :style="{
                            backgroundColor:
                                getPrimaryColorSwatch(primaryColor),
                        }"
                    />
                </label>

                <Input
                    v-model="hexInputValue"
                    class="h-8 rounded-lg bg-white text-sm tracking-tight uppercase"
                    placeholder="#3b82f6"
                    @blur="onHexInputBlur"
                />

                <Button
                    variant="ghost"
                    class="h-8 w-8 rounded-lg p-0 shrink-0"
                    @click="copyColorToClipboard"
                >
                    <Check v-if="copied" :size="14" />
                    <Copy v-else :size="14" />
                </Button>
            </div>
        </div>
        <div>
            <p class="mb-1 text-lg font-medium">
                {{ $t("appearances.color_picker.gray") }}
            </p>
            <div class="gap-3 flex flex-wrap">
                <RadioGroup v-model="grayColor" :default-value="grayColor">
                    <RadioGroupLabel class="sr-only">{{
                        $t("configurator.choose_border_radius")
                    }}</RadioGroupLabel>
                    <div class="gap-2 flex flex-wrap">
                        <RadioGroupOption
                            v-for="option in grayColors"
                            :key="option"
                            v-slot="{ active, checked }"
                            as="template"
                            :value="option"
                        >
                            <RadioGroupLabel
                                class="h-8 w-8 rounded-md shadow-xs"
                                :class="[
                                    active
                                        ? 'ring-iron-700 ring-2 ring-offset-2'
                                        : '',
                                    checked
                                        ? 'bg-black text-white hover:bg-iron-800'
                                        : 'text-iron-900 hover:bg-iron-50',
                                    'rounded-md text-sm font-semibold flex items-center justify-center',
                                ]"
                                :style="{
                                    backgroundColor: colors[option][500],
                                }"
                            >
                                <Check v-if="checked" :size="20" />
                            </RadioGroupLabel>
                        </RadioGroupOption>
                    </div>
                </RadioGroup>
            </div>
        </div>
    </div>
</template>
